import { Flag } from 'lucide-react';
import type { CalendarItem } from '@tempo/core';
import { fromISODate, parseHMSafe, time, todayISO } from '../lib/format';
import { useNow } from '../lib/hooks';

interface Seg {
  item: CalendarItem;
  top: number; // minutes from grid start
  bottom: number;
  lane: number;
  lanes: number;
  clippedTop: boolean;
}

function layout(segs: Omit<Seg, 'lane' | 'lanes'>[]): Seg[] {
  const sorted = [...segs].sort((a, b) => a.top - b.top || b.bottom - a.bottom);
  const out: Seg[] = [];
  let cluster: Seg[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    const lanes = Math.max(1, ...cluster.map((s) => s.lane + 1));
    cluster.forEach((s) => (s.lanes = lanes));
    out.push(...cluster);
    cluster = [];
  };
  for (const s of sorted) {
    if (s.top >= clusterEnd && cluster.length) flush();
    const used = new Set(cluster.filter((c) => c.bottom > s.top).map((c) => c.lane));
    let lane = 0;
    while (used.has(lane)) lane++;
    cluster.push({ ...s, lane, lanes: 1 });
    clusterEnd = Math.max(clusterEnd, s.bottom);
  }
  if (cluster.length) flush();
  return out;
}

export function WeekGrid({
  days,
  items,
  dayStart,
  dayEnd,
  onSelect,
}: {
  days: string[];
  items: CalendarItem[];
  dayStart: string;
  dayEnd: string;
  onSelect: (i: CalendarItem) => void;
}) {
  const now = useNow(60_000);
  const today = todayISO();
  const dayStartMs = days.map((d) => fromISODate(d).startOf('day').toMillis());
  const visible = items.filter((i) => i.kind !== 'deadline' && !i.allDay);

  // Hour range: the active day, stretched to fit anything outside it – except small-hours
  // spill-over (a shift ending 00:30), which is shown as a chip in the top row instead.
  const NIGHT_END_H = 5;
  let startH = Math.floor(parseHMSafe(dayStart) / 60);
  let endH = Math.ceil(parseHMSafe(dayEnd) / 60) || 24;
  if (endH <= startH) endH = 24;
  days.forEach((_, di) => {
    const base = dayStartMs[di];
    for (const i of visible) {
      if (i.kind === 'sleep') continue;
      if (i.start >= base && i.start < base + 86_400_000) {
        const h = (i.start - base) / 3_600_000;
        if (h >= NIGHT_END_H) startH = Math.min(startH, Math.floor(h));
        endH = Math.max(endH, Math.min(24, Math.ceil((i.end - base) / 3_600_000)));
      }
    }
  });
  const hours = Array.from({ length: endH - startH }, (_, i) => startH + i);
  const span = (endH - startH) * 60;

  const columns = days.map((d, di) => {
    const base = dayStartMs[di] + startH * 3_600_000;
    const end = dayStartMs[di] + endH * 3_600_000;
    const segs = visible
      .filter((i) => i.end > base && i.start < end && i.kind !== 'sleep')
      .map((item) => ({
        item,
        top: Math.max(0, (item.start - base) / 60_000),
        bottom: Math.min(span, (item.end - base) / 60_000),
        clippedTop: item.start < base,
      }))
      .filter((s) => s.bottom > s.top);
    const sleep = visible
      .filter((i) => i.kind === 'sleep' && i.end > base && i.start < end)
      .map((i) => ({ top: Math.max(0, (i.start - base) / 60_000), bottom: Math.min(span, (i.end - base) / 60_000) }));
    // Things entirely before the visible range (small hours) go in the top row.
    const early = visible.filter((i) => i.kind !== 'sleep' && i.end > dayStartMs[di] && i.end <= base);
    const markers = [...items.filter((i) => (i.kind === 'deadline' || i.allDay) && i.date === d), ...early];
    return { date: d, base, segs: layout(segs), sleep, markers };
  });

  const H = `var(--hour)`;
  const px = (min: number) => `calc(${min / 60} * ${H})`;

  return (
    <div className="week" style={{ ['--days' as string]: days.length }}>
      <div className="week-head">
        <div />
        {days.map((d) => {
          const dd = fromISODate(d);
          return (
            <div key={d} className={d === today ? 'today' : ''}>
              <div className="dow">{dd.toFormat('ccc')}</div>
              <div className="dnum">{dd.toFormat('d')}</div>
            </div>
          );
        })}
      </div>
      <div className="week-allday">
        <div />
        {columns.map((c) => (
          <div key={c.date}>
            {c.markers.map((m) => (
              <button key={m.id} className="pill truncate" style={{ background: m.color }} onClick={() => onSelect(m)} title={m.title}>
                {m.kind === 'deadline' && <Flag size={10} />} {m.kind === 'deadline' || !m.allDay ? `${time(m.start)} ` : ''}
                {m.title.replace(/^Due: /, '')}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="week-body">
        <div className="week-gutter" style={{ height: px(span) }}>
          {hours.slice(1).map((h) => (
            <span key={h} style={{ top: px((h - startH) * 60) }}>
              {String(h).padStart(2, '0')}:00
            </span>
          ))}
        </div>
        {columns.map((c) => (
          <div key={c.date} className={`week-col ${c.date === today ? 'today' : ''}`} style={{ height: px(span) }}>
            {c.sleep.map((s, i) => (
              <div key={i} className="off" style={{ top: px(s.top), height: px(s.bottom - s.top) }} />
            ))}
            {c.segs.map((s) => {
              const it = s.item;
              const height = s.bottom - s.top;
              const cls = ['wblock', it.kind, it.status === 'done' ? 'done' : '', it.flags.includes('conflict') ? 'conflict' : ''].join(' ');
              return (
                <button
                  key={it.id}
                  className={cls}
                  style={{
                    ['--c' as string]: it.color,
                    top: px(s.top),
                    height: `max(18px, calc(${px(height)} - 2px))`,
                    left: `calc(${(s.lane / s.lanes) * 100}% + 2px)`,
                    width: `calc(${100 / s.lanes}% - 4px)`,
                  }}
                  onClick={() => onSelect(it)}
                  title={`${it.title} ${time(it.start)}–${time(it.end)}`}
                >
                  <div className="t">{it.kind === 'travel' ? `→ ${it.title.replace(/^Travel to /, '')}` : it.title}</div>
                  {height >= 34 && (
                    <div className="s">
                      {s.clippedTop ? `from ${time(it.start)}` : time(it.start)}–{time(it.end)}
                    </div>
                  )}
                </button>
              );
            })}
            {c.date === today && now >= c.base && now <= c.base + span * 60_000 && <div className="week-now" style={{ top: px((now - c.base) / 60_000) }} />}
          </div>
        ))}
      </div>
    </div>
  );
}
